import React, { useState, useCallback, useRef } from 'react';
import { Button, Toast, Picker, Input } from 'antd-mobile';
import type { PickerValue } from 'antd-mobile/es/components/picker';
import { DownOutline, CloseCircleFill } from 'antd-mobile-icons';
import { useTranslation } from 'react-i18next';
import { Column, createRecord, insertRecordBySeq, ResultTable, ScanRecord } from './ResultTable';
import { useScanQueueConsumer } from './useScanQueueConsumer';
import type { PdaScanEvent } from './Pda';
import { parseException, showError, showRepeatedScanError, parseExceptionMessageKey } from 'utils/exceptionUtils';
import { ScanInput } from './ScanInput';
import { isTrackingNumber, isValidBarCode, showBarCodeError } from '../../../utils/BarCodeUtil';
import { playScanFailedAoSound } from '../../../utils/scanUtils';
import { ExceptionDictionaryType } from '../../../graphql/queries/exceptionQueries';
import MultiPhotoGalleryView from './MultiPhotoGalleryView';
import { pdaOpenCamera } from '../../../utils/appRuntime';

export interface ExceptionScanResult {
    success: boolean;
    data?: any;
}

export interface ExceptionScanComponentProps {
    /**
     * 异常字典列表
     */
    exceptionDictionary: ExceptionDictionaryType[];
    /**
     * 异常字典加载状态
     */
    loading?: boolean;
    /**
     * 处理提交的回调函数（需要拍照的异常类型使用）
     * @param trackingNumber 扫描的条码
     * @param selectedExceptionCode 选中的异常代码
     * @param photos 拍摄的照片数组
     */
    onSubmit: (trackingNumber: string, selectedExceptionCode: string, photos: string[]) => Promise<ExceptionScanResult>;
    /**
     * 处理单个扫描的回调函数（不需要拍照的异常类型使用）
     * @param barCode 扫描的条码
     * @param selectedExceptionCode 选中的异常代码
     */
    onSingleScan?: (barCode: string, selectedExceptionCode: string) => Promise<ExceptionScanResult>;
    /**
     * 扫描输入框的占位符
     */
    scanPlaceholder?: string;
    /**
     * 扫描结果表格中条码列的标题
     */
    scanColumnTitle?: string;
    /**
     * 异常选择下拉框的占位符
     */
    exceptionPlaceholder?: string;
    /**
     * 提交按钮文本
     */
    submitButtonText?: string;
    /**
     * 成功记录的额外字段配置
     */
    successExtraFields?: { title: string; key: string }[];
}

export const ExceptionScanComponent: React.FC<ExceptionScanComponentProps> = ({
                                                                                  exceptionDictionary,
                                                                                  loading = false,
                                                                                  onSubmit,
                                                                                  onSingleScan,
                                                                                  scanPlaceholder,
                                                                                  scanColumnTitle,
                                                                                  exceptionPlaceholder,
                                                                                  submitButtonText,
                                                                                  successExtraFields
                                                                              }) => {
    const { i18n, t } = useTranslation();

    // 异常选择相关状态
    const [selectedExceptionCode, setSelectedExceptionCode] = useState<string>('');
    const [pickerVisible, setPickerVisible] = useState(false);

    // 扫描相关状态
    const [successRecords, setSuccessRecords] = useState<ScanRecord[]>([]);
    const [failedRecords, setFailedRecords] = useState<ScanRecord[]>([]);
    const [scanValue, setScanValue] = useState('');

    // 待提交的扫描记录（需要拍照的异常类型使用）
    const [pendingScan, setPendingScan] = useState<string>('');

    // 照片相关状态
    const [photosArray, setPhotosArray] = useState<string[]>([]);

    // 提交状态
    const [isSubmitting, setIsSubmitting] = useState(false);

    // 照片配置常量
    const kMaxSelectorPhotoLength = 2;
    const kMinSelectorPhotoLength = 1;

    // 获取当前语言对应的异常描述
    const getExceptionLabel = (exception: ExceptionDictionaryType): string => {
        const currentLang = i18n.language;
        switch (currentLang) {
            case 'zh-CN':
            case 'zh':
                return exception.secondaryTypeZh;
            case 'es':
                return exception.secondaryTypeEs || exception.secondaryTypeEn;
            default:
                return exception.secondaryTypeEn;
        }
    };

    // 使用 ref 保持回调中始终能读到最新的 state / props
    const selectedExceptionCodeRef = useRef(selectedExceptionCode);
    selectedExceptionCodeRef.current = selectedExceptionCode;
    const exceptionDictionaryRef = useRef(exceptionDictionary);
    exceptionDictionaryRef.current = exceptionDictionary;

    const onProcessScan = useCallback(async (event: PdaScanEvent) => {
        const barCode = event.code;

        // 兼容收起键盘
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        setScanValue(barCode);

        // 检查是否选择了异常原因
        if (!selectedExceptionCodeRef.current) {
            Toast.show({
                content: t('pda.pages.exception.pleaseSelectExceptionReason'),
                icon: <div className="flex items-center gap-2 justify-center">
                    <CloseCircleFill fontSize={20} color='var(--adm-color-danger)'/>
                    <div className="text-base">{t('errors.title')}</div>
                </div>,
                position: 'center',
                duration: 2000
            });
            return;
        }

        const currentExceptionCode = selectedExceptionCodeRef.current;
        const selectedType = exceptionDictionaryRef.current.find(
            exception => exception.exceptionReasonCode === currentExceptionCode
        );

        // 如果不需要拍照，直接提交单个扫描并立即显示结果
        if (!selectedType?.isUploadsImages && onSingleScan) {
            try {
                const result = await onSingleScan(barCode, currentExceptionCode);
                if (result.success) {
                    // 添加到成功记录中并立即显示
                    const extra: Record<string, any> = {};
                    if (successExtraFields && selectedType) {
                        extra.exceptionReason = getExceptionLabel(selectedType);
                    }
                    setSuccessRecords(prev => insertRecordBySeq(prev, createRecord({ value1: barCode, seq: event.seq, ...extra })));
                } else {
                    // 添加到失败记录中
                    setFailedRecords(prev => insertRecordBySeq(prev, createRecord({ value1: barCode, seq: event.seq, failedReason: 'Processing failed' })));
                }
            } catch (error) {
                const messageKey = parseExceptionMessageKey(error);
                let message: string;
                if (messageKey === "errors.business.alreadyScanned") {
                    showRepeatedScanError(error);
                    const { message: errorMessage } = parseException(error);
                    message = errorMessage;
                } else {
                    const { title, message: errorMessage } = parseException(error);
                    playScanFailedAoSound();
                    showError({ title, message: errorMessage });
                    message = errorMessage;
                }
                setFailedRecords(prev => insertRecordBySeq(prev, createRecord({ value1: barCode, seq: event.seq, failedReason: message })));
            }
        } else {
            // 需要拍照，设置为最新扫描的单号
            setPendingScan(barCode);
        }
    }, [onSingleScan, successExtraFields, t]);

    const { enqueueScan } = useScanQueueConsumer({
        maxConcurrency: 1,
        onProcess: onProcessScan,
    });

    // 使用 Hook 形式的翻译函数设置默认值
    const finalScanPlaceholder = scanPlaceholder || t('pda.pages.exception.trackingNO') || 'Tracking NO';
    const finalScanColumnTitle = scanColumnTitle || t('pda.pages.exception.trackingNO') || 'Tracking NO';
    const finalExceptionPlaceholder = exceptionPlaceholder || t('pda.pages.exception.selectExceptionReason') || 'Select Exception Reason';
    const finalSubmitButtonText = submitButtonText || t('pda.common.submit') || 'Submit';

    // 转换异常字典为 Picker 选项
    const exceptionOptions = exceptionDictionary.map(exception => ({
        label: getExceptionLabel(exception),
        value: exception.exceptionReasonCode
    }));

    // 获取当前选中的异常类型
    const getSelectedExceptionType = (): ExceptionDictionaryType | undefined => {
        return exceptionDictionary.find(exception => exception.exceptionReasonCode === selectedExceptionCode);
    };

    // 判断当前选中的异常类型是否需要上传图片
    const shouldShowPhotoGallery = (): boolean => {
        const selectedType = getSelectedExceptionType();
        return selectedType?.isUploadsImages || false;
    };

    // 处理多图画廊的连拍功能
    const handleMultiPhotoGalleryBurstCapture = async () => {
        const currentPhotoCount = photosArray.length;
        const remainingPhotoCount = Math.max(0, kMaxSelectorPhotoLength - currentPhotoCount);
        const minSelectionCount = currentPhotoCount >= kMinSelectorPhotoLength ? 0 : kMinSelectorPhotoLength - currentPhotoCount;

        // 使用PDA专用相机，传递国际化文本
        const photoTitles = [
            t('pda.pages.exception.firstPhoto') || '第一张照片',
            t('pda.pages.exception.secondPhoto') || '第二张照片',
            t('pda.pages.exception.thirdPhoto') || '第三张照片'
        ];

        await pdaOpenCamera(
            remainingPhotoCount, 
            minSelectionCount,
            t('pda.pages.exception.requiredPhotos') || '', // showTips 参数
            photoTitles, // photoTitles 数组
            t('pda.pages.exception.complete') || '', // doneButtonText
            async (photos: string[]) => {
                try {
                    setPhotosArray(prevPhotos => [...prevPhotos, ...photos]);
                } catch (error) {
                    Toast.show({ content: t('pda.pages.exception.multiPhotoProcessingFailed'), position: 'center' });
                }
            }
        );
    };

    // 处理删除照片
    const handleDeletePhoto = (index: number) => {
        setPhotosArray(prevPhotos => prevPhotos.filter((_, i) => i !== index));
    };

    // 处理异常选择变化
    const handleExceptionChange = (value: PickerValue[]) => {
        const selectedCode = String(value[0]);
        setSelectedExceptionCode(selectedCode);
        setPickerVisible(false);
        // 获取新选择的异常类型
        const newSelectedType = exceptionDictionary.find(exception => exception.exceptionReasonCode === selectedCode);
        // 如果从不需要拍照的异常切换到需要拍照的异常，且当前有扫描值，则设置为待提交状态
        if (newSelectedType?.isUploadsImages && scanValue && !pendingScan) {
            setPendingScan(scanValue);
        }
        // 切换异常类型时清空照片
        setPhotosArray([]);
        // 只有在切换到不需要拍照的异常类型时才清空待提交扫描
        if (!newSelectedType?.isUploadsImages) {
            setPendingScan('');
        }
    };

    // 获取选中的异常标签
    const getSelectedLabel = () => {
        if (!selectedExceptionCode) {
            return finalExceptionPlaceholder;
        }
        const selectedOption = exceptionOptions.find(opt => opt.value === selectedExceptionCode);
        return selectedOption?.label || finalExceptionPlaceholder;
    };

    // 处理提交
    const handleSubmit = async () => {
        // 验证异常原因是否已选择
        if (!selectedExceptionCode) {
            Toast.show({
                content: t('pda.pages.exception.pleaseSelectExceptionReason'),
                icon: <div className="flex items-center gap-2 justify-center">
                    <CloseCircleFill fontSize={20} color='var(--adm-color-danger)'/>
                    <div className="text-base">{t('errors.title')}</div>
                </div>,
                position: 'center',
                duration: 2000
            });
            return;
        }

        // 验证是否有待提交的扫描记录
        if (!pendingScan) {
            Toast.show({
                content: t('pda.pages.exception.noScanRecords') || 'No scan records',
                icon: <div className="flex items-center gap-2 justify-center">
                    <CloseCircleFill fontSize={20} color='var(--adm-color-danger)'/>
                    <div className="text-base">{t('errors.title')}</div>
                </div>,
                position: 'center',
                duration: 2000
            });
            return;
        }

        // 检查是否需要上传图片
        const selectedType = getSelectedExceptionType();
        if (selectedType?.isUploadsImages && photosArray.length === 0) {
            Toast.show({
                content: t('pda.pages.exception.pleaseUploadPhotos'),
                icon: <div className="flex items-center gap-2 justify-center">
                    <CloseCircleFill fontSize={20} color='var(--adm-color-danger)'/>
                    <div className="text-base">{t('errors.title')}</div>
                </div>,
                position: 'center',
                duration: 2000
            });
            return;
        }

        setIsSubmitting(true);

        try {
            // 使用待提交的扫描记录
            const result = await onSubmit(pendingScan, selectedExceptionCode, photosArray);

            if (result.success) {
                // 提交成功后，将待提交的扫描记录移动到成功记录中显示
                const selectedType = getSelectedExceptionType();
                const extra: Record<string, any> = {};
                if (successExtraFields && selectedType) {
                    extra.exceptionReason = getExceptionLabel(selectedType);
                }
                const newSuccessRecord = createRecord({ value1: pendingScan, ...extra });
                
                setSuccessRecords(prev => [newSuccessRecord, ...prev]);
                
                // 清空待提交状态
                setPendingScan('');
                setPhotosArray([]);
                setScanValue('');

                Toast.show({
                    content: t('pda.common.submitSuccess') || 'Submit successful',
                    position: 'center',
                    duration: 2000
                });
            }
        } catch (error) {
            const { title, message } = parseException(error);
            showError({ title, message });

            // 将待提交的扫描记录移动到失败记录中
            const failedItem = createRecord({ value1: pendingScan, failedReason: message });
            setFailedRecords(prev => [failedItem, ...prev]);
            setPendingScan('');
        } finally {
            setIsSubmitting(false);
        }
    };

    // 动态拼接成功列，扩展字段放到最后
    const successColumns: Column[] = [
        { title: finalScanColumnTitle, key: 'value1' },
        { title: t('pda.common.time'), key: 'time' },
        ...(successExtraFields || [])
    ];

    const failedColumns = [
        { title: finalScanColumnTitle, key: 'value1' },
        { title: t('pda.common.time'), key: 'time' },
        { title: t('pda.common.failedReason'), key: 'failedReason' }
    ];

    return (
        <div className="flex flex-col h-full bg-white">
            {/* 异常选择下拉框 */}
            <div
                className="flex items-center mb-2 border rounded cursor-pointer"
                onClick={() => {
                    if (!loading) {
                        setPickerVisible(true);
                    }
                }}
            >
                <Input
                    className="flex-1 p-2"
                    placeholder={finalExceptionPlaceholder}
                    value={selectedExceptionCode ? getSelectedLabel() : ''}
                    readOnly
                    disabled={loading}
                    style={{ cursor: 'pointer' }}
                />
                <Button
                    fill={"none"}
                    className="ml-2 bg-transparent border-0"
                    onClick={() => {
                        if (!loading) {
                            setPickerVisible(true);
                        }
                    }}
                    disabled={loading}
                >
                    <DownOutline fontSize={24} />
                </Button>
            </div>

            {/* Picker 组件 */}
            <Picker
                columns={[exceptionOptions]}
                visible={pickerVisible}
                onClose={() => setPickerVisible(false)}
                onConfirm={handleExceptionChange}
                value={selectedExceptionCode ? [selectedExceptionCode] : []}
                title={t('pda.pages.exception.selectExceptionReason')}
            />

            {/* 扫描输入区域 */}
            <div className="mb-4">
                <ScanInput
                    placeholder={finalScanPlaceholder}
                    value={scanValue}
                    onScan={(code) => {
                        if (isValidBarCode(code)) {
                            enqueueScan(code);
                        } else {
                            showBarCodeError(code);
                        }
                    }}
                />
            </div>

            {/* 照片画廊区域 */}
            {shouldShowPhotoGallery() && (
                <div className="mb-4">
                    <div className="text-sm font-medium text-gray-700 mb-3 px-4">
                        {t('pda.pages.exception.exceptionPhotos')}
                    </div>
                    <MultiPhotoGalleryView
                        photos={photosArray}
                        onDelete={handleDeletePhoto}
                        onNativeBurstCapture={handleMultiPhotoGalleryBurstCapture}
                        maxDisplayCount={kMaxSelectorPhotoLength}
                    />
                </div>
            )}

            {/* 提交按钮 - 只有需要拍照的异常类型才显示 */}
            {shouldShowPhotoGallery() && (
                <div className="mb-4">
                    <Button
                        block
                        color="primary"
                        size="large"
                        onClick={handleSubmit}
                        loading={isSubmitting}
                        disabled={loading || isSubmitting || !pendingScan}
                    >
                        {finalSubmitButtonText}
                    </Button>
                </div>
            )}

            {/* 结果表格 */}
            <div className="flex-1 overflow-hidden">
                <ResultTable
                    successRecords={successRecords}
                    failedRecords={failedRecords}
                    successColumns={successColumns}
                    failedColumns={failedColumns}
                />
            </div>
        </div>
    );
};